import { clearTimeout, setTimeout } from 'timers'
import { Vue, Component, Prop, Ref } from 'vue-property-decorator'
import Timeout = NodeJS.Timeout

@Component
export default class Slider extends Vue {
  @Prop({ default: 'slider_default', type: String })
  readonly sliderClass!: string

  @Ref()
  readonly track!: Element

  @Ref()
  readonly slider!: Element

  @Prop({ type: Boolean, default: false })
  readonly moveToEnd?: boolean

  @Prop({ type: Boolean, default: false })
  readonly isAutoPlay?: boolean

  @Prop({ type: Number, default: 5000 })
  readonly interval?: number

  currentSlide: number = 0
  trackOffset: number = 0
  lastTrackOffset: number = 0
  isMove: boolean = false
  timerId: Timeout | undefined

  get sliderClassObject () {
    return {
      slider: true,
      [this.sliderClass]: true
    }
  }

  get trackStyle () {
    return {
      transform: `translateX(${this.trackOffset}px)`
    }
  }

  get trackClass () {
    return {
      slider__track: true,
      slider__track_transition: !this.isMove
    }
  }

  get count (): number {
    return this.$slots.default?.length ?? 0
  }

  countShowElem (): number {
    return Math.round(this.track.clientWidth / this.calcWidthElem())
  }

  mounted () {
    window.addEventListener('mousemove', this.mousemove)
    window.addEventListener('resize', this.moveToNearest)
    this.initTouth()
    if (this.isAutoPlay) {
      setTimeout(() => {
        this.next()
      }, this.interval as number)
    }
  }

  beforeDestroy () {
    window.removeEventListener('mousemove', this.mousemove)
    window.removeEventListener('resize', this.moveToNearest)
  }

  async initTouth () {
    const Hammer = require('hammerjs')
    const mc = new Hammer.Manager(this.track)
    mc.add(new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 0 }))

    mc.on('swipeleft', () => !this.isMove ? this.next() : null)
    mc.on('swiperight', () => !this.isMove ? this.prev() : null)
  }

  moveTo (number: number, skipAnimation: boolean = false) {
    if (this.isAutoPlay && number > this.count - 1) {
      number = 0
    }

    const moveToEndNotUse = (number < 0 || number > this.count - 1) && !this.moveToEnd
    const moveToEndUse = number < 0 || (this.moveToEnd && number > this.count - this.countShowElem())

    if (moveToEndNotUse || moveToEndUse) {
      return
    }

    this.trackOffset = this.calcWidthElem() * -number
    this.currentSlide = number
    if (this.isAutoPlay) {
      clearTimeout(this.timerId as Timeout)
      this.timerId = setTimeout(() => {
        this.next()
      }, this.interval as number)
    }
    this.$emit('change', this.currentSlide)
  }

  next () {
    this.moveTo(this.currentSlide + 1)
  }

  prev () {
    this.moveTo(this.currentSlide - 1)
  }

  calcWidthElem (): number {
    if (Array.isArray(this.$slots.default)) {
      const elem = this.$slots.default[0].elm as HTMLElement
      return elem.getBoundingClientRect().width
    }
    return 1
  }

  calcNearestIndex (): number {
    return Math.round(Math.abs(this.trackOffset) / this.calcWidthElem())
  }

  moveToNearest () {
    this.moveTo(this.calcNearestIndex())
  }

  mousedown () {
    this.isMove = true
  }

  mouseup () {
    this.isMove = false
    this.moveToNearest()
  }

  mouseleave () {
    this.isMove = false
    this.moveToNearest()
  }

  mousemove (event: any) {
    if (!Array.isArray(this.$slots.default)) {
      return
    }

    if (!this.isMove) {
      return
    }

    const elemWidth = this.calcWidthElem()
    const trackWidth = elemWidth * this.count
    const minOffset = elemWidth / 2
    const maxOffset = elemWidth / 2 - trackWidth
    const targetOffset = this.trackOffset + event.movementX
    if (targetOffset > minOffset) {
      return
    }

    if (targetOffset <= maxOffset) {
      return
    }

    const endTrack = trackWidth - (elemWidth * this.countShowElem())

    if (this.moveToEnd && (Math.abs(targetOffset) > endTrack)) {
      return
    }

    this.trackOffset = targetOffset
  }

  render () {
    return (
      <div class={this.sliderClassObject} ref="slider">
        <div class="slider__track-wrapper">
          <div {...{
            ref: 'track',
            class: this.trackClass,
            style: this.trackStyle,
            on: {
              mousedown: this.mousedown,
              mouseup: this.mouseup,
              mouseleave: this.mouseleave
            }
          }}>
            {this.$slots.default}
          </div>
        </div>
      </div>
    )
  }
}
